export type EnglishGlossaryTerm = {
  id: string;
  term: string;
  alias?: string;
  definition: string;
  context?: string;
  relatedIds?: string[];
  group: "core" | "role" | "marketplace" | "engineering" | "console" | "billing-governance";
};

export const EN_GLOSSARY_TERMS: EnglishGlossaryTerm[] = [
  {
    id: "hotline",
    term: "Hotline",
    alias: "productized capability endpoint",
    definition:
      "A Hotline is the capability unit in CALL ANYTHING: a standardized contract that bundles identity, billing, approval, observability, and routing into the protocol layer so any agent can discover, call, and settle it.",
    context:
      "A Hotline may expose an MCP server, an HTTP endpoint, or a SKILL.md wrapper, but those are access surfaces. The product itself is the Hotline.",
    relatedIds: ["caller", "responder", "marketplace", "result-package"],
    group: "core",
  },
  {
    id: "caller",
    term: "Caller",
    alias: "agent-side caller",
    definition:
      "A Caller is the AI agent or agent team that invokes a Hotline. It discovers available capabilities through catalog APIs and reads a shared result_package shape instead of writing custom client code for every provider.",
    relatedIds: ["responder", "hotline", "result-package", "caller-console"],
    group: "role",
  },
  {
    id: "responder",
    term: "Responder",
    alias: "capability operator",
    definition:
      "A Responder is the operator behind a Hotline, usually a one-person company or a small team. It keeps the core implementation private, runs the business logic locally, and returns protocol-shaped results to Callers.",
    relatedIds: ["caller", "hotline", "opc", "supervisor"],
    group: "role",
  },
  {
    id: "opc",
    term: "OPC",
    alias: "one-person company",
    definition:
      "OPC stands for one-person company: an individual packaging private expertise into digital twins that agents can call 24/7, with protocol-level billing and settlement built in.",
    context:
      "CALL ANYTHING treats the OPC as a first-class seller persona rather than a side case hidden behind generic API infrastructure.",
    relatedIds: ["responder", "digital-twin", "hotline", "marketplace"],
    group: "core",
  },
  {
    id: "digital-twin",
    term: "Digital Twin",
    alias: "OPC digital twin",
    definition:
      "A digital twin is the service form of an OPC's expertise after it has been packaged as one or more Hotlines that agents can call continuously without exposing the underlying workflow or prompt stack.",
    relatedIds: ["opc", "responder", "hotline", "supervisor"],
    group: "core",
  },
  {
    id: "marketplace",
    term: "Marketplace",
    alias: "public hotline directory",
    definition:
      "The Marketplace is the public directory of Hotlines on callanything.xyz. It lets agents discover responders, compare capability summaries, inspect versions, and decide what to call next.",
    relatedIds: ["hotline", "caller", "responder"],
    group: "marketplace",
  },
  {
    id: "supervisor",
    term: "Supervisor",
    alias: "local runtime daemon",
    definition:
      "The Supervisor is the local process that mounts Hotlines, manages sessions, forwards calls, and records operational events for both Caller and Responder roles.",
    relatedIds: ["caller", "responder", "hotline", "result-package"],
    group: "engineering",
  },
  {
    id: "hotline-id",
    term: "Hotline ID",
    alias: "versioned capability identifier",
    definition:
      "A Hotline ID is the globally unique, versioned name of a Hotline. It lets a Caller pin exactly which capability and version it wants to invoke.",
    relatedIds: ["hotline", "result-package", "catalog"],
    group: "engineering",
  },
  {
    id: "result-package",
    term: "Result Package",
    alias: "shared protocol result envelope",
    definition:
      "A result package is the shared response structure defined by the protocol. It gives every Hotline the same outer shape for status, output, timing, and identity signals, so agents only learn one parsing model.",
    relatedIds: ["hotline", "caller", "hotline-id"],
    group: "engineering",
  },
  {
    id: "catalog",
    term: "Catalog",
    alias: "available hotline list",
    definition:
      "A Catalog is the Hotline list exposed by the Supervisor. A Caller uses it to determine which capabilities are currently installed, routable, and callable in its runtime.",
    relatedIds: ["caller", "marketplace", "hotline-id"],
    group: "engineering",
  },
  {
    id: "template-bundle",
    term: "Template Bundle",
    alias: "hotline capability bundle",
    definition:
      "A Template Bundle is the minimum descriptive package for publishing a Hotline: summary, schemas, examples, risk notes, and adapter shape. Review and discovery both depend on it.",
    relatedIds: ["responder", "hotline", "marketplace"],
    group: "engineering",
  },
  {
    id: "approval-policy",
    term: "Approval Policy",
    alias: "human review rule",
    definition:
      "An Approval Policy is the rule set a Caller team applies to a Hotline so high-risk calls can require human review before execution or settlement.",
    relatedIds: ["caller-console", "hotline", "caller"],
    group: "console",
  },
  {
    id: "caller-console",
    term: "Caller Console",
    alias: "agent-team control plane",
    definition:
      "The Caller Console is the control surface for agent teams. It covers discovery, approvals, runtime history, billing, and version awareness across every Hotline the team uses.",
    relatedIds: ["caller", "approval-policy", "hotline"],
    group: "console",
  },
  {
    id: "responder-console",
    term: "Responder Console",
    alias: "operator workspace",
    definition:
      "The Responder Console is the operating surface for the seller side. It is where an OPC manages Hotline versions, pricing, review status, and usage signals for its digital twins.",
    relatedIds: ["responder", "opc", "hotline", "marketplace"],
    group: "console",
  },
  {
    id: "call-credit",
    term: "Call Credit",
    alias: "internal points unit",
    definition:
      "Call Credit is the internal billing unit CALL ANYTHING uses today. Callers top up a balance, calls debit it, and Responders earn back into the same balance. Caller-side spend and Responder-side earnings live in one unified balance per user — what you earn is immediately what you can spend, no transfer step needed. Phase 1 does NOT support fiat withdrawal.",
    context:
      "Call Credit is a protocol-layer abstraction. The platform uses points and never directly handles fiat or integrates with payment providers. Whether to add fiat is a future-stage decision that is independent of the protocol contract.",
    relatedIds: ["pricing-models", "auto-refund", "trust-tier"],
    group: "billing-governance",
  },
  {
    id: "pricing-models",
    term: "Three pricing models",
    alias: "fixed_price · base_plus_duration · base_plus_tokens",
    definition:
      "The protocol direction names three pricing models. fixed_price = lump-sum 'I don't care how you do it, just deliver and I pay' — the silent default. base_plus_duration = base + responder-self-reported time. base_plus_tokens = base + a hotline-defined unit count (LLM tokens / pages / images / API calls — not bound to any specific provider). Every model is value-pricing, not cost-pass-through.",
    context:
      "fixed_price is the no-declaration default. The 'token' in base_plus_tokens is hotline-defined semantics — it does not have to be an LLM provider's actual unit.",
    relatedIds: ["hotline", "call-credit", "auto-refund"],
    group: "billing-governance",
  },
  {
    id: "auto-refund",
    term: "Auto-refund",
    alias: "protocol-mandated full refund",
    definition:
      "The protocol mandates 5 machine-decidable failure classes that trigger a full refund without caller action: result UNVERIFIED, request TIMED_OUT, result FAILED-non-retryable, the hotline is FROZEN while the caller's request is in-flight, and the result is rejected by platform content review. No half-refunds.",
    context:
      "Subjective dissatisfaction goes through a manual dispute queue, accepted only for untrusted/trusted tier hotlines; verified hotlines refuse one-sided complaints to prevent abuse. Caller-side appeal rate over 10% in 30 days auto-suspends appeal rights.",
    relatedIds: ["call-credit", "trust-tier", "result-package"],
    group: "billing-governance",
  },
  {
    id: "trust-tier",
    term: "Trust Tier",
    alias: "hotline behavioural trust ladder",
    definition:
      "The protocol defines four trust tiers — untrusted / trusted / verified / frozen — orthogonal to admin review's review_status. Tier moves are machine-driven (drift detection, dual-call sampling, content review signals, dispute outcomes), never bought through ratings. Each tier has its own per-call cap, refund policy strictness, and settlement delay (e.g. untrusted earnings sit in pending_credit for ~7 days).",
    context:
      "trust_tier and admin review's review_status are orthogonal: the former determines 'how much you can charge once listed and how strict refunds are', the latter determines 'whether you appear in the catalog'. Zero-trust core: there is NO 'approved → instantly verified' shortcut.",
    relatedIds: ["auto-refund", "pricing-models", "responder-responsibility"],
    group: "billing-governance",
  },
  {
    id: "responder-responsibility",
    term: "Responder responsibility",
    alias: "operator-side accountability",
    definition:
      "On a value-pricing rail responders take protocol-level responsibility for the output. The protocol does NOT split 'subjective malice' from 'model-uncontrolled output' — once an output triggers a risk class (prompt injection / executable payload / PII leak / disallowed category / schema violation), the responder takes the trust_tier hit regardless of intent.",
    context:
      "The dual of 'I don't care how you do it, deliver and I pay' (caller view) is 'whatever you used, you own its output' (responder view). It defines the operator role.",
    relatedIds: ["pricing-models", "auto-refund", "trust-tier"],
    group: "billing-governance",
  },
];

export const EN_GLOSSARY_GROUPS: Array<{
  id: EnglishGlossaryTerm["group"];
  label: string;
  intro: string;
}> = [
  {
    id: "core",
    label: "Core Concepts",
    intro: "The minimum vocabulary required to understand CALL ANYTHING.",
  },
  {
    id: "role",
    label: "Protocol Roles",
    intro: "Caller and Responder are the two sides of every Hotline interaction.",
  },
  {
    id: "marketplace",
    label: "Marketplace",
    intro: "How public discovery works at the protocol layer.",
  },
  {
    id: "engineering",
    label: "Engineering Terms",
    intro: "Implementation-level concepts that appear in docs, APIs, and examples.",
  },
  {
    id: "console",
    label: "Operations",
    intro: "Control-plane concepts for teams and operators.",
  },
  {
    id: "billing-governance",
    label: "Billing & Governance",
    intro:
      "Points-based settlement, the three pricing models, auto-refunds, and trust tiers — how the protocol's 'deliver and I pay' actually clears.",
  },
];
